import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Avatar,
  Rating,
  Divider,
  Dialog,
  DialogContent,
  IconButton
} from '@mui/material';
import AnimatedTitle from './common/AnimatedTitle';
import ReviewsSection from './salon/ReviewsSection';
import {
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  Euro as EuroIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

const DesktopSalonProfile = ({ salon, onBooking }) => {
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const formatSchedule = () => {
    if (!salon.schedule) return [];
    
    const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const daysOfWeek = {
      monday: 'Lundi',
      tuesday: 'Mardi',
      wednesday: 'Mercredi',
      thursday: 'Jeudi',
      friday: 'Vendredi',
      saturday: 'Samedi',
      sunday: 'Dimanche'
    };
    
    return daysOrder.map(day => {
      const hours = salon.schedule[day];
      if (!hours) return null;
      
      return {
        day: daysOfWeek[day],
        hours: hours.closed ? 'Fermé' : `${hours.open} - ${hours.close}`,
        isOpen: !hours.closed
      };
    }).filter(Boolean);
  };

  const handlePhoneCall = () => {
    window.location.href = `tel:${salon.phone}`;
  };

  const handleEmail = () => {
    window.location.href = `mailto:${salon.email || ''}`;
  };

  const openPhotoModal = (index) => {
    setSelectedPhotoIndex(index);
    setPhotoModalOpen(true);
  };

  const closePhotoModal = () => {
    setPhotoModalOpen(false);
  };

  const nextPhoto = () => {
    if (salon.photos && selectedPhotoIndex < salon.photos.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    }
  };

  const prevPhoto = () => {
    if (selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      nextPhoto();
    } else if (event.key === 'ArrowLeft') {
      prevPhoto();
    } else if (event.key === 'Escape') {
      closePhotoModal();
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fafaf9' }}>
      {/* Hero Section with Photos */}
      <Box sx={{ maxWidth: '1200px', mx: 'auto', px: 4, pt: 4 }}>
        {salon.photos && salon.photos.length > 0 ? (
          <Box sx={{ mb: 4 }}>
            {/* Photo Gallery Grid */}
            <Box sx={{ display: 'flex', gap: 2, height: 400 }}>
              {/* Main photo */}
              <Box sx={{ flex: '2' }}>
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    position: 'relative',
                    border: '1px solid #e7e5e4',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                  onClick={() => openPhotoModal(0)}
                >
                  <Box
                    component="img"
                    src={salon.photos[0]}
                    alt={salon.salonName}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  {/* Subtle overlay for text readability */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(28, 25, 23, 0.7))',
                      p: 3,
                      color: 'white'
                    }}
                  >
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      {salon.salonName}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <Chip
                        onClick={() => {}}
                        label={salon.category}
                        sx={{
                          backgroundColor: '#F5F5DC',
                          color: '#1c1917',
                          fontWeight: 600
                        }}
                      />
                      <Box display="flex" alignItems="center">
                        <LocationIcon sx={{ mr: 1, fontSize: 20 }} />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {salon.city}, {salon.region}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
              
              {/* Side photos */}
              <Box sx={{ flex: '1', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {salon.photos.slice(1, 4).map((photo, index) => (
                  <Box
                    key={index}
                    sx={{
                      flex: 1,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid #e7e5e4',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                    onClick={() => openPhotoModal(index + 1)}
                  >
                    <Box
                      component="img"
                      src={photo}
                      alt={`Photo ${index + 2}`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: 300,
              background: 'linear-gradient(135deg, #F5F5DC 0%, #E8E8D0 30%, #D6D3D1 70%, #1c1917 100%)',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
              border: '1px solid #e7e5e4'
            }}
          >
            <Box textAlign="center">
              <Typography variant="h1" sx={{ fontSize: 60, mb: 2, color: 'white' }}>📷</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
                {salon.salonName}
              </Typography>
              <Chip
                onClick={() => {}}
                label={salon.category} 
                sx={{ backgroundColor: '#F5F5DC', color: '#1c1917', fontWeight: 600 }}
              />
            </Box>
          </Box>
        )}

        {/* Info Section */}
        <Paper sx={{ p: 4, mb: 4, border: '1px solid #e7e5e4' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationIcon sx={{ mr: 2, color: '#78716c' }} />
                <Typography variant="h6" sx={{ color: '#1c1917' }}>
                  {salon.address ? `${salon.address}, ` : ''}{salon.city}, {salon.region}
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" mb={3}>
                <PhoneIcon sx={{ mr: 2, color: '#78716c' }} />
                <Typography variant="h6" sx={{ color: '#1c1917' }}>
                  {salon.phone}
                </Typography>
              </Box>

              {salon.description && (
                <Typography variant="body1" sx={{ color: '#57534e', lineHeight: 1.6 }}>
                  {salon.description}
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CalendarIcon />}
                onClick={onBooking}
                sx={{
                  backgroundColor: '#F5F5DC',
                  color: '#1c1917',
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#E8E8D0'
                  }
                }}
              >
                Réserver maintenant
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Main Content */}
      <Box sx={{ maxWidth: '1200px', mx: 'auto', px: 4, pb: 6 }}>
        <Grid container spacing={4}>
          {/* Schedule */}
          <Grid item xs={12} md={4}>
            <Card sx={{ border: '1px solid #e7e5e4' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#1c1917', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon sx={{ color: '#78716c' }} />
                  Horaires
                </Typography>
                
                <Box>
                  {formatSchedule().map(({ day, hours, isOpen }) => (
                    <Box key={day} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      py: 1.5,
                      borderBottom: '1px solid #f5f5f4',
                      '&:last-child': { borderBottom: 'none' }
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1c1917' }}>
                        {day}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: isOpen ? '#16a34a' : '#dc2626',
                          fontWeight: 500
                        }}
                      >
                        {hours}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Services */}
          <Grid item xs={12} md={8}>
            <Card sx={{ border: '1px solid #e7e5e4' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#1c1917' }}>
                  Services & Tarifs
                </Typography>
                
                {salon.services && salon.services.length > 0 ? (
                  <Grid container spacing={3}>
                    {salon.services.map((service, index) => (
                      <Grid item xs={12} sm={6} key={service.id || index}>
                        <Paper
                          sx={{
                            p: 3,
                            border: '1px solid #e7e5e4',
                            backgroundColor: '#ffffff',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
                            }
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1c1917' }}>
                            {service.name}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                            <Chip
                              onClick={() => {}}
                              label={`${service.duration} min`}
                              size="small"
                              sx={{ 
                                backgroundColor: '#f5f5f4',
                                color: '#57534e',
                                fontWeight: 500
                              }}
                            />
                            <Chip
                              onClick={() => {}}
                              label={`${service.price}€`}
                              size="small"
                              sx={{
                                backgroundColor: '#F5F5DC',
                                color: '#1c1917',
                                fontWeight: 600
                              }}
                            />
                          </Box>
                          
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={() => onBooking(service)}
                            sx={{
                              backgroundColor: '#1c1917',
                              color: 'white',
                              py: 1.5,
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#44403c'
                              }
                            }}
                          >
                            Réserver
                          </Button>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body1" sx={{ color: '#78716c' }}>
                    Aucun service configuré
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Additional Photos */}
            {salon.photos && salon.photos.length > 4 && (
              <Card sx={{ mt: 4, border: '1px solid #e7e5e4' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#1c1917' }}>
                    Plus de photos
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {salon.photos.slice(4).map((photo, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Box
                          component="img"
                          src={photo}
                          alt={`Photo ${index + 5}`}
                          sx={{
                            width: '100%',
                            height: 200,
                            borderRadius: 2,
                            objectFit: 'cover',
                            border: '1px solid #e7e5e4',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.05)'
                            }
                          }}
                          onClick={() => openPhotoModal(index + 4)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Reviews */}
            <Box sx={{ mt: 4 }}>
              <ReviewsSection salon={salon} isMobile={false} />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Photo Modal */}
      <Dialog
        open={photoModalOpen}
        onClose={closePhotoModal}
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
            margin: 0,
            maxHeight: '100vh',
            maxWidth: '100vw'
          }
        }}
        onKeyDown={handleKeyDown}
      >
        <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          {/* Close button */}
          <IconButton
            onClick={closePhotoModal}
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Previous button */}
          {selectedPhotoIndex > 0 && (
            <IconButton
              onClick={prevPhoto}
              sx={{
                position: 'absolute',
                left: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1000,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          {/* Next button */}
          {salon.photos && selectedPhotoIndex < salon.photos.length - 1 && (
            <IconButton
              onClick={nextPhoto}
              sx={{
                position: 'absolute',
                right: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1000,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          )}

          {/* Photo */}
          {salon.photos && salon.photos[selectedPhotoIndex] && (
            <Box
              component="img"
              src={salon.photos[selectedPhotoIndex]}
              alt={`Photo ${selectedPhotoIndex + 1}`}
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: 2
              }}
            />
          )}

          {/* Photo counter */}
          {salon.photos && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                px: 2,
                py: 1,
                borderRadius: 2
              }}
            >
              <Typography variant="body2">
                {selectedPhotoIndex + 1} / {salon.photos.length}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DesktopSalonProfile;